/**
 * Canonical event-date helpers (pure, no imports).
 *
 * This file exists twice and the two copies MUST stay byte-identical:
 *   - src/utils/eventDate.ts
 *   - functions/src/utils/eventDate.ts
 * Check with: node functions/scripts/check-event-date.mjs
 *
 * See docs/work/2026-11/2026-11-02-meeting-couple-glitches/date-02-plan.md
 */

export type EventDateCheckStatus =
  | 'ok'
  | 'no-forecasted'
  | 'title-mismatch'
  | 'unexpected-forecasted-time'
  | 'no-title-date';

export interface EventDateCheck {
  status: EventDateCheckStatus;
  /** YYYY-MM-DD parsed from the Daylite title, or null. */
  titleDate: string | null;
  /** YYYY-MM-DD derived from Daylite `forecasted`, or null. */
  forecastedDate: string | null;
  /** The raw Daylite `forecasted` value, or null. */
  forecastedRaw: string | null;
  /** Server timestamp (Firestore Timestamp / FieldValue). */
  checkedAt?: unknown;
}

export interface EventDateCheckAck {
  status: EventDateCheckStatus;
  titleDate: string | null;
  forecastedDate: string | null;
  by?: string;
  at?: unknown;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isRealCalendarDate(y: number, m: number, d: number): boolean {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (m < 1 || m > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** First 10 chars (YYYY-MM-DD) of a Daylite `forecasted` string, if valid; otherwise null. */
export function deriveEventDate(forecasted: unknown): string | null {
  if (typeof forecasted !== 'string') return null;
  if (!/^\d{4}-\d{2}-\d{2}(T|$)/.test(forecasted)) return null;
  const iso = forecasted.slice(0, 10);
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return isRealCalendarDate(y, m, d) ? iso : null;
}

const NEW_YORK_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

/**
 * False only when the reading is ambiguous: a timestamp whose UTC calendar date (what deriveEventDate uses) differs
 * from its New York calendar date. Daylite keeps a time of day on Forecasted (prod, 2026-11-04: UTC hours 04-19 on
 * every non-placeholder event, none ambiguous), so the time itself is not a signal. Exact UTC midnight is the
 * system-created GA placeholder form and reads as its UTC date by design.
 */
export function forecastedTimeIsExpected(forecasted: unknown): boolean {
  if (typeof forecasted !== 'string') return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(forecasted)) return true;
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/.test(forecasted)) return true;
  const ms = Date.parse(forecasted);
  if (Number.isNaN(ms)) return false;
  return NEW_YORK_DATE.format(new Date(ms)) === forecasted.slice(0, 10);
}

/** Date at the very start of a Daylite name ("MM-DD-YY ..."), as YYYY-MM-DD, or null. */
export function parseTitleDate(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const match = /^(\d{2})-(\d{2})-(\d{2})(?!\d)/.exec(name.trimStart());
  if (!match) return null;
  const m = Number(match[1]);
  const d = Number(match[2]);
  const y = 2000 + Number(match[3]);
  if (!isRealCalendarDate(y, m, d)) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** "Sat, Mar 13, 2027" for a YYYY-MM-DD string; "Date TBD" for null or invalid input. */
export function formatEventDateForMessage(iso: string | null | undefined): string {
  const derived = typeof iso === 'string' ? deriveEventDate(iso) : null;
  if (!derived) return 'Date TBD';
  const y = Number(derived.slice(0, 4));
  const m = Number(derived.slice(5, 7));
  const d = Number(derived.slice(8, 10));
  const weekday = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${weekday}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

/** MM-DD-YY for a YYYY-MM-DD string, or '' when invalid. */
export function formatShortTitleDate(iso: string | null | undefined): string {
  const derived = typeof iso === 'string' ? deriveEventDate(iso) : null;
  if (!derived) return '';
  return `${derived.slice(5, 7)}-${derived.slice(8, 10)}-${derived.slice(2, 4)}`;
}

/** Replaces every [event-date] token with the formatted date (or "Date TBD"). */
export function replaceEventDateToken(text: string, iso: string | null | undefined): string {
  return text.replace(/\[event-date\]/g, formatEventDateForMessage(iso));
}

/**
 * Precedence: no-forecasted > title-mismatch > unexpected-forecasted-time > no-title-date > ok.
 */
export function computeDateCheck(
  name: unknown,
  forecasted: unknown
): Pick<EventDateCheck, 'status' | 'titleDate' | 'forecastedDate'> {
  const titleDate = parseTitleDate(name);
  const forecastedDate = deriveEventDate(forecasted);
  let status: EventDateCheckStatus;
  if (!forecastedDate) status = 'no-forecasted';
  else if (titleDate && titleDate !== forecastedDate) status = 'title-mismatch';
  else if (!forecastedTimeIsExpected(forecasted)) status = 'unexpected-forecasted-time';
  else if (!titleDate) status = 'no-title-date';
  else status = 'ok';
  return {status, titleDate, forecastedDate};
}

/** An acknowledgement holds only while status, titleDate and forecastedDate all still match. */
export function isDateCheckAcknowledged(
  check: Pick<EventDateCheck, 'status' | 'titleDate' | 'forecastedDate'> | null | undefined,
  ack: Pick<EventDateCheckAck, 'status' | 'titleDate' | 'forecastedDate'> | null | undefined
): boolean {
  if (!check || !ack) return false;
  return (
    check.status === ack.status &&
    (check.titleDate ?? null) === (ack.titleDate ?? null) &&
    (check.forecastedDate ?? null) === (ack.forecastedDate ?? null)
  );
}
